"use client"
import { useState, useEffect } from "react"
import styled from "styled-components"
import { motion, AnimatePresence } from "framer-motion"
import { useInView } from "react-intersection-observer"
import { FiAward, FiX } from "react-icons/fi"

// --- STYLED COMPONENTS ---

const CertificatesSection = styled.section`
  padding: 6rem 0;
  background: transparent; // To see the global gradient
  position: relative;
`

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: clamp(2.5rem, 5vw, 3.5rem);
    font-weight: 700;
    color: #1B1B1E;
    margin-bottom: 1rem;
    font-family: 'Poppins', sans-serif;

    .highlight {
      background: linear-gradient(135deg, #2A9D8F,rgb(7, 125, 111)); // Gold to orange gradient
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  p {
    font-size: 1.125rem;
    color: #1B1B1E;
    opacity: 0.8;
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

const CertificatesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`

const CertificateCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(38, 70, 83, 0.15);
  border-radius: 1rem;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    border-color: #E9C46A;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1.414 / 1; // A4 paper aspect ratio
  background-size: cover;
  background-position: center;
  background-image: ${({ $image }) => `url(${$image})`};
  border-bottom: 1px solid rgba(38, 70, 83, 0.15);
`

const CardContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`

const CertificateTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1B1B1E;
  margin-bottom: 0.5rem;
`

const IssuerName = styled.p`
  font-size: 0.9rem;
  color: #1B1B1E;
  opacity: 0.8;
  margin-bottom: 1rem;
  flex-grow: 1;
`

const ViewButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #264653;
  font-weight: 500;
  margin-top: auto;
  align-self: flex-start;
`

// Modal Styles
const ModalBackdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 0.5rem;
    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.4);
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: -15px;
  right: -15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #F4F1DE;
  color: #1B1B1E;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  z-index: 1001;
`

// --- DATA ---
const certificatesData = [
  {
    title: "Software Development (Full Stack)",
    issuer: "kLab & ICT Chamber (TekHer Program)",
    image: "/klab.png",
  },
  {
    title: "Back-end Application Development",
    issuer: "SheCanCODE & Mastercard Foundation",
    image: "/Shecancode.png",
  },
  {
    title: "IoT Certification (LoRaWan)",
    issuer: "Hubiquitous & Fablab Rwanda",
    image: "/IOT.png",
  },
  {
    title: "Entrepreneurship, ICT & Life Skills",
    issuer: "Hanga Hubs & ICT Chamber",
    image: "/ICT.png",
  },
];


// --- MAIN COMPONENT ---

const Certificates = () => {
  const [selectedCert, setSelectedCert] = useState(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  // Effect to disable body scroll when modal is open
  useEffect(() => {
    if (selectedCert) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedCert]);


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <>
      <CertificatesSection id="certificates" ref={ref}>
        <Container>
          <SectionHeader variants={itemVariants} initial="hidden" animate={inView ? "visible" : "hidden"}>
            <h2>
              Certificates & <span className="highlight">Credentials</span>
            </h2>
            <p>A collection of my professional certifications and training achievements.</p>
          </SectionHeader>

          <CertificatesGrid variants={containerVariants} initial="hidden" animate={inView ? "visible" : "hidden"}>
            {certificatesData.map((cert) => (
              <CertificateCard
                key={cert.title}
                variants={itemVariants}
                onClick={() => setSelectedCert(cert.image)}
                layoutId={`card-container-${cert.title}`}
              >
                <ImageContainer $image={cert.image} />
                <CardContent>
                  <CertificateTitle>{cert.title}</CertificateTitle>
                  <IssuerName>{cert.issuer}</IssuerName>
                  <ViewButton>
                    <FiAward size={16} />
                    View Certificate
                  </ViewButton>
                </CardContent>
              </CertificateCard>
            ))}
          </CertificatesGrid>
        </Container>
      </CertificatesSection>

      <AnimatePresence>
        {selectedCert && (
          <ModalBackdrop
            onClick={() => setSelectedCert(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ModalContent
              onClick={(e) => e.stopPropagation()} // Prevents closing modal when clicking the image
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <CloseButton onClick={() => setSelectedCert(null)}><FiX /></CloseButton>
              <img src={selectedCert} alt="Full size certificate" />
            </ModalContent>
          </ModalBackdrop>
        )}
      </AnimatePresence>
    </>
  );
};

export default Certificates;